//#region Imports
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PhoneForwardedIcon from '@mui/icons-material/PhoneForwarded';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  SelectChangeEvent,
  Skeleton,
  Tab,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { green } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import clsx from 'clsx';
import {
  FieldArray,
  FieldArrayRenderProps,
  Formik,
  FormikHelpers,
  FormikProps,
  FormikTouched,
} from 'formik';
import React, {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';
//#endregion
import phoneLib from 'google-libphonenumber';

import Axios from '../../../common/axiosInterceptor';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import { flatten } from '../../../common/flatten';
import {
  FormikCheckbox,
  FormikSelect,
  FormikSwitch,
  FormikTextField,
} from '../../../common/formik-material-ui';
import '../../../common/formik-material-ui/isValidE164Number';
import { Logger } from '../../../common/Logger';
import {
  loadableEnum,
  useMakePHPRequest,
} from '../../../common/useMakePHPRequest';
import { isUrl } from '../../../common/validators';
import { IBusinessUser, IUserAtom, user } from '../../../recoil/atoms/user';
import {
  makePHPRequest,
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import {
  countryGroups,
  countryList,
  ICountryList,
} from '../../subpages/countryList';
import TabLabel from '../general/TabLabel';
import { useSnackbarWithStyles } from '../notifications/SnackbarProviderWithStyles';
import { RecordNow } from '../recorder/RecordNow';

import { WizardModal } from './WizardModal';

const PNF = phoneLib.PhoneNumberFormat;
const phoneUtil = phoneLib.PhoneNumberUtil.getInstance();
type DeviceInQuestion = Pick<
  IBusinessDevices,
  | 'deviceId'
  | 'deviceTextId'
  | 'dialingSelected'
  | 'forwardSelected'
  | 'ringSelected'
  | 'sipPass'
  | 'sipUser'
> &
  Partial<
    Pick<
      IBusinessDevices,
      'countrycode' | 'deviceName' | 'forwardNumber' | 'phoneType'
    >
  > & { userid?: string };

interface NewDevice {
  countrycode: string;
  deviceName: string;
  forwardNumber: string;
  phoneType: string;
  userid: string;
}

/**
 * Check if there are errors in current tab
 *
 * @param thisTabIndex Current tab
 * @param errors Formik error object
 * @param tabToFieldsLookup Double array used to lookup which fields belongs to which tab
 *
 * @returns Name of field if error detected else undefined
 *
 * @example
 *  isErrorInTab(tabIndex, fomik.errors, tabToFields);
 */
export const isErrorInTab = (
  thisTabIndex: number,
  errors: Record<string, any>,
  tabToFieldsLookup: string[][]
): string | undefined =>
  Object.keys(errors).find((field) =>
    tabToFieldsLookup[thisTabIndex]?.includes(field)
  );

/**
 * Retrieve first tab that has offending error
 *
 * @param errors Formik error object
 * @param tabToFieldsLookup Double array used to lookup which fields belongs to which tab
 *
 * @returns Index of tab that has error
 *
 * @example
 *  getErrorTabIndex(fomik.errors);
 */
export const getErrorTabIndex = (
  errors: Record<string, any>,
  tabToFieldsLookup: string[][]
): number | undefined => {
  for (const field in errors) {
    for (const index in tabToFieldsLookup) {
      if (tabToFieldsLookup[index].includes(field)) {
        return +index;
      }
    }
  }
};
//#region Styles
const useStyles = makeStyles((theme: Theme) => ({
  header: {
    color: 'var(--primary)', // pull color from primary
    fontWeight: 'bold',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  button: {
    marginLeft: theme.spacing(1), // space between fields
  },
  reset: {
    margin: 0,
    padding: 0,
  },
  section: {
    marginTop: theme.spacing(2),
  },
  field: {
    flex: '1 1 0',
    marginTop: theme.spacing(1), // space between fields
    marginBottom: theme.spacing(1), // space between fields
  },
  defaultAction: {
    marginTop: theme.spacing(2),
  },
  helperText: {
    marginBottom: theme.spacing(2),
  },
  skeleton: {
    backgroundColor: 'lightgrey',
  },
  table: {
    marginTop: theme.spacing(2), // align with fields
    marginBottom: theme.spacing(2), // align with fields
  },
  formGroupForDevices: {
    flexDirection: 'row',
  },
  alignRight: {
    textAlign: 'right',
  },
  deleteIconButton: {
    color: theme.custom.button.delete.main,
  },
  buttonProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -22,
    marginLeft: -22,
  },
  spaceBelowHeader: {
    marginTop: theme.spacing(2),
  },
  spaceVertically: {
    marginBottom: theme.spacing(2),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  spaceHorizontally: {
    marginRight: theme.spacing(2),
    '&:last-child': {
      marginRight: 0,
    },
  },
  deviceButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  shortenIcon: {
    margin: '4px 0px', // make hover circle round
  },
  helpIcon: {
    margin: '12px 0px', // make hover circle round
  },
  iconSpacer: {
    width: 43.5,
  },
  hr: {
    width: '100%',
    background:
      theme?.palette?.mode === 'dark'
        ? '1px solid #737373'
        : '1px solid #cbd2da',
    marginTop: 0,
  },
  ringScrolling: {
    marginBottom: 0,
    '&::-webkit-scrollbar': {
      '-webkit-appearance': 'none',
      width: 7,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      backgroundColor: 'rgba(0, 0, 0, .5)',
      boxShadow: '0 0 1px rgba(255, 255, 255, .5)',
    },
    height: 152, // height will cut off half the text to show scrollable
    overflow: 'auto',
  },
  tabs: {},
  tab: {
    maxWidth: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiTab-wrapper': {
      flexDirection: 'row',
    },
    '&[tabindex="0"]': {
      backgroundColor: theme.palette.divider,
    },
  },
  tabHeader: {
    width: '100%',
    '&.MuiTab-textColorInherit.Mui-disabled': {
      opacity: 1,
      '& .MuiTab-wrapper': {
        justifyContent: 'flex-start',
        margin: '10px 0',
      },
    },
  },
  tabIcon: {
    marginRight: 15,
  },
  tabError: {
    '& .MuiTabs-indicator': {
      backgroundColor: 'red',
    },
  },
  scrollWithin: {
    '&::-webkit-scrollbar': {
      '-webkit-appearance': 'none',
      width: 7,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 4,
      backgroundColor: 'rgba(0, 0, 0, .5)',
      boxShadow: '0 0 1px rgba(255, 255, 255, .5)',
    },
    maxHeight: 'calc(100vh - 210px)',
    overflow: 'auto',
  },
  navigation: {
    padding: 0,
    backgroundColor: theme.palette.divider,
  },
  twoColumnLayout: {
    display: 'flex',
    '& div:first-child': {
      marginLeft: 0, // full-width if only one column occupied
    },
    '& div:last-child': {
      marginRight: 0, // full-width if only one column occupied
    },
  },
  leftColumn: {
    flex: 1,
    // marginRight: theme.spacing(1),
  },
  rightColumn: {
    display: 'flex',
    flex: 1,
    marginLeft: theme.spacing(1),
  },
}));
//#endregion

//#region Interfaces
type TAudioType = 'mp3' | 'tts' | 'record';

export interface IRingGroupSettings {
  forward: {
    'user-forward-ring': string | boolean;
    'user-forward-duration': string;
    'user-press-one-ring': string | boolean;
    members: string[]; // derived data
  };
  simultaneous: {
    'user-simultaneous-ring': string | boolean;
    'user-simultaneous-duration': string;
    members: string[]; // derived data
  };
  greeting: {
    type: 'mp3' | 'tts' | 'none' | 'record';
    value: string;
  };
  userid: string | undefined;
}

/**
 * Raw output from getBusinessDevices REST call
 */
export interface IGetBusinessDevice {
  DID: string;
  countrycode: string;
  cred: string;
  deviceType: string;
  deviceid: string;
  isdefault: number;
  phoneType: string;
  sipUser: string;
  sipUserId: number;
  userId: number;
}

/**
 * Transformed output from getBusinessDevices REST call
 */
export interface IBusinessDevices {
  countrycode: string;
  sipUser: string;
  sipPass: string;
  deviceName: string;
  forwardNumber: string;
  deviceId: string;
  deviceTextId: string;
  phoneType: string;
  ringSelected: boolean;
  forwardSelected: boolean;
  dialingSelected: boolean;
}

interface IFormObject {
  submitQueueEditPayload: {
    greetingType: string;
    greeting: string;
  };
  submitQueueGreetingMp3File: {
    files: string;
  };
  submitNewDevice: {
    forwardNumber: string;
    countrycode: string;
    deviceName: string;
    phoneType: string;
    userid: string;
  };
  ringGroupSettings: IRingGroupSettings;
  kixieDevices: IBusinessDevices[];
  forwardDevices: IBusinessDevices[];
  dialingDeviceId: string;
  accountSettings?: any;
}
//#endregion

//#region Helper Functions
export const DevicesSkeleton = () => {
  const classes = useStyles();
  return (
    <div>
      <Typography component="div" variant={'h3'}>
        <Skeleton className={classes.skeleton} />
      </Typography>
      <Typography component="div" variant={'h3'}>
        <Skeleton className={classes.skeleton} />
      </Typography>
      <Typography component="div" variant={'h3'}>
        <Skeleton className={classes.skeleton} />
      </Typography>
    </div>
  );
};

/**
 * Create device label on the fly since users can edit name/number
 *
 * @param name Name of device
 * @param number Phonenumber of device
 */
export const getDeviceLabel = (name: string, number: string) =>
  number ? `${name} (${number})` : name;

export const removeBackslashEscaping = (text?: string | null) => {
  if (!text) {
    return text;
  }
  return text.replace(/\\(.)/g, '$1');
};

async function uploadFile(payload: { [key: string]: any }) {
  const data = new FormData();
  // append key/value to form data
  Object.entries(payload).forEach(([key, value]) => {
    data.append(key, value);
  });

  const requestConfig: AxiosRequestConfig = {
    method: 'post',
    url: `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
    headers: {
      'content-type': 'multipart/form-data',
    },
    withCredentials: true,
    data,
  };

  // make ajax request
  const result = await Axios(requestConfig);

  // log post
  Logger.debug({
    methodName: payload.call,
    requestConfig,
    result,
  });
}

/**
 * Determine if any deviced is checked, exclude current device (via deviceId)
 *
 * @param deviceId Current device
 * @param checked Current action state
 * @param devices List of devices
 */
const isOneChecked = (deviceId: string, checked: boolean, devices: any[]) => {
  return (
    !!devices
      .filter(
        (device: IBusinessDevices) => device.deviceId !== deviceId // skip this checkbox since we're evaluating before check takes effect
      )
      .find((device: IBusinessDevices) => device.ringSelected) || checked // count current check state
  );
};
//#endregion

interface IDevicesAndRoutingModalProps {
  id?: string;
  show: boolean;
  onHide: () => void;
  initialTabIndex?: number;
}

export function DevicesAndRoutingModal({
  id,
  show,
  onHide,
  initialTabIndex = 0,
}: IDevicesAndRoutingModalProps) {
  const classes = useStyles();

  const [tooltipIsOpen, setTooltipIsOpen] = useState<number>(-1);

  //#region Refs
  //============
  const formikTouched = useRef<FormikTouched<IFormObject>>();
  //#endregion

  //#region States
  //==============
  const [submitState, setSubmitState] = useState<loadableEnum>(
    loadableEnum.EMPTY
  );
  const [showAddNumber, setShowAddNumber] = useState<boolean>(false);
  const [showAddKixieCell, setShowAddKixieCell] = useState<boolean>(false);
  const [showAddKixieDesk, setShowAddKixieDesk] = useState<boolean>(false);
  const [kixieDeviceIndex, setKixieDeviceIndex] = useState(-1);
  const [deletedDeviceIds, setDeletedDevicesIds] = useState<string[]>([]);
  //#endregion

  //#region Recoil
  //==============
  const [userAtom, setUserAtom] = useRecoilStateLoadable<
    IUserAtom | Record<string, unknown>
  >(user);
  const userAtomContents = userAtom.contents as IUserAtom;
  const [filenames, setFilenames] = useState({
    greeting: '',
  }); // fields cannot be null/undefined
  const [filepaths, setFilepaths] = useState({
    greeting: '',
  }); // fields cannot be null/undefined
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, any>>({});
  //#endregion

  //#region Hooks
  //=============
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const phpRequest = useMakePHPRequest({
    userAtom: userAtomContents,
  });
  //#endregion

  //#region Ajax
  //============
  // Ring Group Settings
  // -------------------
  const ringGroupSettingsLoadable = useRecoilValueLoadable(
    makePHPRequest({
      methodName: methodNameEnum.GET_USER_RING_GROUP_SETTINGS,
      payloadJsonString: JSON.stringify({
        userid: id,
      }),
    })
  );
  const refetchToggleForRingGroupSettings = useSetRecoilState(
    refetchToggle(methodNameEnum.GET_USER_RING_GROUP_SETTINGS)
  );
  const ringGroupSettingsCached = useRef<any>();
  ringGroupSettingsCached.current = useMemo(
    () =>
      ringGroupSettingsLoadable.state === 'loading'
        ? ringGroupSettingsCached.current
        : ringGroupSettingsLoadable.contents?.data?.result,
    [ringGroupSettingsLoadable]
  );

  // Business Devices
  // ----------------
  // const businessDevicesLoadable = useRecoilValueLoadable(
  //   makePHPRequest({
  //     methodName: methodNameEnum.GET_BUSINESS_DEVICES,
  //   })
  // );
  const data = {
    call: methodNameEnum.GET_BUSINESS_DEVICES,
  };
  const businessDevicesLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_DEVICES,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const refetchToggleForBusinessDevices = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DEVICES)
  );
  const businessDevicesCached = useRef<any>([]);
  businessDevicesCached.current = useMemo(
    () =>
      businessDevicesLoadable.state === 'loading'
        ? businessDevicesCached.current
        : businessDevicesLoadable.contents?.response?.data?.result.map(
            (device: any) => ({
              ...device,
              // stringified in order for equality comparisons to work
              isdefault: device.isdefault.toString(),
              sipUserId: device.sipUserId.toString(),
              userId: device.userId.toString(),
            })
          ) ?? [],
    [businessDevicesLoadable]
  );
  //#endregion
  //#region Form objects
  //====================
  const businessDevices = useMemo<IBusinessDevices[]>(
    () =>
      businessDevicesCached.current
        ?.filter((device: any) => device.userId === id)
        .map((device: any) => ({
          countrycode: device.countrycode ?? 'US',
          sipUser: [' Web RTC', 'mobileapp'].includes(device.phoneType)
            ? ''
            : device.sipUser,
          sipPass: [' Web RTC', 'mobileapp'].includes(device.phoneType)
            ? ''
            : device.cred,
          deviceName:
            device.deviceType === 'webrtc' ? 'PowerCall' : device.deviceType,
          forwardNumber: device.DID ?? '',
          deviceId: device.sipUserId,
          deviceTextId: device.deviceid,
          phoneType:
            device.phoneType === ' Web RTC'
              ? 'PowerCall'
              : device.phoneType === 'cell phone'
              ? 'cell'
              : device.phoneType,
          ringSelected:
            ringGroupSettingsCached.current?.simultaneous.members.includes(
              device.sipUserId
            ) ?? true,
          forwardSelected:
            ringGroupSettingsCached.current?.linear.member === device.sipUserId,
          dialingSelected: device.isdefault === '1',
        })),
    // cannot listen to refs since it does not register state changes
    [businessDevicesLoadable, ringGroupSettingsLoadable]
  );
  // all fields must default to not null/undefined or else react will give uncontrolled prop error
  const ringGroupSettings = useMemo<IRingGroupSettings>(
    () => ({
      forward: {
        'user-forward-ring':
          ringGroupSettingsCached.current?.linear.on === 'true',
        'user-forward-duration':
          ringGroupSettingsCached.current?.linear.duration ?? '',
        'user-press-one-ring':
          ringGroupSettingsCached.current?.linear.pressone === 'true',
        members: [ringGroupSettingsCached.current?.linear.member ?? ''], // derived data
      },
      simultaneous: {
        'user-simultaneous-ring':
          ringGroupSettingsCached.current?.simultaneous.on === 'true',
        'user-simultaneous-duration':
          ringGroupSettingsCached.current?.simultaneous.duration ?? '',
        members: ringGroupSettingsCached.current?.simultaneous.members ?? [], // derived data
      },
      greeting: {
        type: ringGroupSettingsCached.current?.Details?.DIDGreeting
          ? isUrl(ringGroupSettingsCached.current?.Details?.DIDGreeting)
            ? 'mp3'
            : 'tts'
          : 'none',
        value:
          removeBackslashEscaping(
            ringGroupSettingsCached.current?.Details?.DIDGreeting
          ) ?? '',
      },
      userid: id,
    }),
    // cannot listen to refs since it does not register state changes
    [id, ringGroupSettingsLoadable]
  );

  const [
    myBusinessUsersCached,
    myBusinessUsersLoadable,
    refetchToggleMyBusinessUsers,
  ] = makeApiRequestCached<IBusinessUser | undefined>(
    {
      endpoint: endpointEnum.GET_BUSINESS_USERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_USERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.find(
        (user) => user.userid == id
      ) ?? cached.current,
    undefined,
    [id]
  );

  const [filenamesVm, setFilenamesVm] = useState({
    voicemail: myBusinessUsersCached.current?.greeting ?? '',
  });
  const [filepathsVm, setFilepathsVm] = useState({
    voicemail: myBusinessUsersCached.current?.greeting ?? '',
  });
  const [uploadedFilesVm, setUploadedFilesVm] = useState({
    voicemail: '',
  });

  useEffect(() => {
    setFilenamesVm({
      voicemail:
        (isUrl(myBusinessUsersCached.current?.greeting) &&
          myBusinessUsersCached.current?.greeting
            ?.split('/')
            .slice(-1)
            .pop()) ||
        '',
      // filename from uri/path
    });
    setFilepathsVm({
      voicemail: myBusinessUsersCached.current?.greeting ?? '',
    });
    //   replave with settings seelctor
  }, [myBusinessUsersLoadable]);
  /**
   * Map of tabIndex to formik fields
   *
   * NOTE: must update if initialValues changes
   */
  const tabToFields = [
    [
      'submitQueueEditPayload',
      'submitQueueGreetingMp3File',
      'submitNewDevice',
      'ringGroupSettings',
      'dialingDeviceId',
    ],
    ['forwardDevices'],
    ['kixieDevices'],
  ];

  const initialValues = useMemo<IFormObject>(
    () => ({
      submitQueueEditPayload: {
        greetingType: ringGroupSettings.greeting.type,
        greeting: ringGroupSettings.greeting.value,
      },
      submitQueueGreetingMp3File: {
        files: '',
        name: filenames?.greeting ?? '',
      },
      submitNewDevice: {
        forwardNumber: '',
        countrycode: 'US',
        deviceName: '',
        phoneType: 'cell',
        userid: id ?? '',
      },
      ringGroupSettings,
      kixieDevices: businessDevices.filter(
        (device: IBusinessDevices) => !device.forwardNumber
      ),
      forwardDevices: businessDevices
        .filter((device: IBusinessDevices) => device.forwardNumber)
        .map((device) => ({
          ...device,
          forwardNumber: device.forwardNumber.replace('+', ''),
        })),
      dialingDeviceId:
        businessDevices.find(
          (device: IBusinessDevices) => device.dialingSelected
        )?.deviceTextId ?? '',
      accountSettings: {
        voicemailType: isUrl(myBusinessUsersCached.current?.greeting)
          ? 'mp3'
          : 'tts',
        greeting: isUrl(myBusinessUsersCached.current?.greeting)
          ? 'Please leave a message at the tone'
          : removeBackslashEscaping(myBusinessUsersCached.current?.greeting) ??
            'Please leave a message at the tone.' ??
            '',
      },
      submitVoicemailMp3File: {
        files: '',
        name: myBusinessUsersCached.current?.greeting ?? '',
      },
    }),
    [
      id,
      ringGroupSettingsLoadable,
      businessDevicesLoadable,
      myBusinessUsersCached,
    ]
  );
  /**
   * Validation schema for Inbound Call Flow
   */
  const validationSchema = useMemo(
    () =>
      Yup.object({
        submitQueueGreetingMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
        submitVoicemailMp3File: Yup.object({
          name: Yup.string().checkFileSize(
            20_000_000,
            'File must be at or below 20mb'
          ),
        }),
        forwardDevices: Yup.array().of(
          Yup.object().shape({
            deviceName: Yup.string().required('Required'),
            forwardNumber: Yup.string()
              .required('Required')
              .matches(
                /^[1-9]\d{1,14}$/g,
                'Invalid Phone Number.  Please use E.164 phone number format.'
              )
              .isValidE164Number('Number format not valid for country'),
          })
        ),
        kixieDevices: Yup.array().of(
          Yup.object().shape({
            deviceName: Yup.string().required('Required'),
          })
        ),
        ...(showAddNumber
          ? {
              submitNewDevice: Yup.object().shape({
                forwardNumber: Yup.string()
                  .required('Required')
                  // .matches(
                  //   /^[1-9]\d{1,14}$/g,
                  //   'Invalid Phone Number.  Please use E.164 phone number format.'
                  // ),
                  .isValidE164Number('Number format not valid for country'),
                deviceName: Yup.string().required('Required'),
              }),
            }
          : {}), // do not indclude submitNewDevice if not needed
      }),
    [showAddNumber]
  );
  //#endregion
  const mutation = useMutation(getApiMutation(userAtomContents, setUserAtom));
  //#region Event Handlers
  //======================
  const handleSubmit = useCallback(
    async (values: IFormObject, action: FormikHelpers<IFormObject>) => {
      // Determine if any new forward devices have been added
      const newDevices: NewDevice[] = [];
      for (let i = 0; i < values.forwardDevices.length; i++) {
        const curDevice = values.forwardDevices[i] as DeviceInQuestion;

        if (!curDevice?.deviceId) {
          // This device was added on the ui and needs to be added with submitNewDevice
          newDevices.push(curDevice as NewDevice);
        }
      }

      for (let i = 0; i < newDevices.length; i++) {
        await handleSubmitNewDevice(newDevices[i]);
      }
      setSubmitState(loadableEnum.LOADING);
      let errorMessage = '';

      // update mp3 if touched and no failures detected
      // ----------------------------------------------
      if (
        !errorMessage &&
        values.ringGroupSettings?.greeting?.type === 'mp3' &&
        uploadedFiles.greeting?.[0]
      ) {
        await uploadFile({
          call: methodNameEnum.SUBMIT_USER_GREETING_MP3_FILE,
          businessid: userAtomContents?.businessid,
          apiKey: userAtomContents?.apikey,
          userid: id,
          mp3: uploadedFiles.greeting[0],
        });
      }

      if (
        !errorMessage &&
        values.ringGroupSettings?.greeting?.type === 'record' &&
        formikTouched.current?.ringGroupSettings?.greeting?.type &&
        uploadedFiles.greeting?.[0]
      ) {
        const formData = new FormData();
        // ***all params are required****
        formData.append('webm', uploadedFiles.greeting[0]);
        formData.append('businessid', userAtomContents?.businessid as any);
        formData.append('apikey', userAtomContents?.apikey);
        formData.append('userid', id as any); // user id of agent being changed
        formData.append('id', id as any); // user id of agent being changed
        formData.append('call', 'HandleAudioRecording'); // call needed to upload RECORD NOW file
        formData.append('iid', 0 as any); // id for voicemail drop when editing otherwise if adding put 0 - APPLYS ONLY TO VOICEMAIL DROP send 0 everywhere else
        formData.append('name', ''); // for vm drop name MUST be name of voicemail drop
        formData.append('type', 'user-greeting'); // type options: [ivr, user-greeting, agent-voicemail, group-voicemail, group-greeting, agent-voicemail-drop // for edit vm drop, new-agent-voicemail-drop // for new vm drop,  queue-greeting]
        const _response3 = await Axios.post(
          `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
          formData
        );
      }
      // console.log(ringGroupSettingsFlattened);

      const ringDetectedForKixieDevice = formikTouched.current?.kixieDevices?.find(
        (device) => device?.ringSelected
      );

      const ringDetectedForForwardDevice = formikTouched.current?.forwardDevices?.find(
        (device) => device?.ringSelected
      );

      // update ring group settings if touched and no failures detected
      // --------------------------------------------------------------
      if (
        !errorMessage &&
        (formikTouched.current?.ringGroupSettings ||
          ringDetectedForKixieDevice ||
          ringDetectedForForwardDevice)
      ) {
        const ringGroupSettingsFlattened = Object.entries(
          flatten({
            // add members in immutable way
            ...values.ringGroupSettings,
            // forward: {
            //   ...values.ringGroupSettings.forward,
            //   members: values.forwardDevices
            //     .filter((device) => device.forwardSelected)
            //     .map((device) => device.deviceId),
            // },
            simultaneous: {
              ...values.ringGroupSettings.simultaneous,
              members: values.kixieDevices
                .concat(values.forwardDevices)
                .filter((device) => device.ringSelected)
                .map((device) => device.deviceId),
            },
          })
        ).reduce(
          (submitObject: any, [key, value]: [string, any]) => ({
            ...submitObject,
            [key]: typeof value === 'boolean' ? (value ? 'on' : 'off') : value,
          }),
          {}
        );

        if (
          ringGroupSettingsFlattened &&
          ringGroupSettingsFlattened['greeting[type]'] === 'none'
        ) {
          ringGroupSettingsFlattened['greeting[value]'] = '';
        }

        // update ring group settings
        const { response, exception, status } = await phpRequest({
          methodName: methodNameEnum.WEB_UPDATE_KIXIE_USER_RING_GROUP,
          payload: ringGroupSettingsFlattened,
        });

        // check successful
        const isSuccess =
          status === loadableEnum.HAS_VALUE && response?.data?.meta?.success;

        if (!isSuccess) {
          errorMessage = 'Failed to update ring group settings';
          Logger.error(
            {
              errorMessage,
              internalMessage: response?.data?.meta?.message,
              exception,
            },
            'DevicesAndRouting:handleSubmit'
          );
        }
      }
      // edit user voicemail if touched and no failures detected
      // ------------------------------------------------------
      const { voicemailType, greeting } =
        (values as any)?.accountSettings ?? {};
      // is the vm type tts
      if (voicemailType === 'tts') {
        // make request for updating tts value
        const userid = Number(id);
        const data = {
          tts: greeting,
          userid,
        };
        await phpRequest({
          methodName: methodNameEnum.UPDATE_USER_VOICEMAIL,
          payload: data,
        });
      }

      if (voicemailType === 'mp3' && uploadedFilesVm.voicemail) {
        // if the vm type selected is not tts we assume its mp3 or record and is there an uploaded/recorded file
        const formData = new FormData();
        formData.append('call', 'submitUserVoicemailMp3File');
        formData.append('mp3', uploadedFilesVm.voicemail[0]);
        formData.append('businessid', String(userAtomContents?.businessid));
        formData.append('apikey', userAtomContents?.apikey);
        formData.append('userid', String(id));
        await Axios.post(
          `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
          formData
        );
      }
      if (voicemailType === 'record' && uploadedFilesVm.voicemail) {
        const formData = new FormData();
        // ***all params are required****
        formData.append('webm', uploadedFilesVm.voicemail[0]);
        formData.append('businessid', String(userAtomContents?.businessid));
        formData.append('apikey', userAtomContents?.apikey);
        formData.append('userid', String(id)); // user id of agent being changed
        formData.append('id', String(id)); // user id of agent being changed
        formData.append('call', 'HandleAudioRecording'); // call needed to upload RECORD NOW file
        formData.append('iid', '0'); // id for voicemail drop when editing otherwise if adding put 0 - APPLYS ONLY TO VOICEMAIL DROP send 0 everywhere else
        formData.append(
          'name',
          myBusinessUsersCached.current?.fname +
            ' ' +
            myBusinessUsersCached.current?.lname
        ); // for vm drop name MUST be name of voicemail drop
        formData.append('type', 'agent-voicemail'); // type options: [ivr, user-greeting, agent-voicemail, group-voicemail, group-greeting, agent-voicemail-drop // for edit vm drop, new-agent-voicemail-drop // for new vm drop,  queue-greeting]
        await Axios.post(
          `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`,
          formData
        );
      }

      const editDetectedForKixieDevice = formikTouched.current?.kixieDevices?.find(
        (device) => device?.deviceName
      );
      // edit kixie devices if touched and no failures detected
      // ------------------------------------------------------
      if (
        !errorMessage &&
        formikTouched.current?.kixieDevices &&
        editDetectedForKixieDevice
      ) {
        for (const index in formikTouched.current?.kixieDevices ?? []) {
          if (formikTouched.current?.kixieDevices?.[index]) {
            const device = values.kixieDevices[index];
            try {
              const number = phoneUtil.parseAndKeepRawInput(
                device.forwardNumber,
                device.countrycode
              );
              const E164 = phoneUtil.format(number, PNF.E164);
              // update ring group settings
              const { response, exception, status } = await phpRequest({
                methodName: methodNameEnum.EDIT_DEVICE,
                payload: {
                  forwardNumber: E164,
                  deviceName: device.deviceName,
                  countrycode: device.countrycode,
                  phoneType: device.phoneType,
                  deviceid: device.deviceId,
                  userid: id,
                  edittype: 'sip',
                },
              });
              // check successful
              const isSuccess =
                status === loadableEnum.HAS_VALUE &&
                response?.data?.meta?.success;

              // exit if failed
              if (!isSuccess) {
                errorMessage = 'Failed to edit kixie number';
                Logger.error(
                  {
                    errorMessage,
                    internalMessage: response?.data?.meta?.message,
                    exception,
                  },
                  'DevicesAndRouting:handleSubmit'
                );
                break;
              }
            } catch {
              // eslint-disable-next-line no-console
              console.error(
                'Error parsing phone number with phoneUtil.parseAndKeepRawInput'
              );
            }
          }
        }
      }

      // edit forward devices if touched and no failures detected
      // --------------------------------------------------------
      const editedDevices = getEditedForwardDevices(values, businessDevices);

      if (!errorMessage && editedDevices.length) {
        for (const device of editedDevices) {
          const number = phoneUtil.parseAndKeepRawInput(
            device.forwardNumber,
            device.countrycode
          );
          const countryCode = number.getCountryCode();
          if (!device.forwardNumber.startsWith(String(countryCode))) {
            errorMessage = 'Number must start with correct country code.';
            break;
          }
          const E164 = phoneUtil.format(number, PNF.E164);
          // update ring group settings
          const { response, exception, status } = await phpRequest({
            methodName: methodNameEnum.EDIT_DEVICE,
            payload: {
              forwardNumber: E164,
              deviceName: device.deviceName,
              countrycode: device.countrycode,
              phoneType: device.phoneType,
              deviceid: device.deviceId,
              userid: id,
              edittype: 'forward',
            },
          });

          // check successful
          const isSuccess =
            status === loadableEnum.HAS_VALUE && response?.data?.meta?.success;

          // exit if failed
          if (!isSuccess) {
            errorMessage = 'Failed to edit forward number';
            Logger.error(
              {
                errorMessage,
                internalMessage: response?.data?.meta?.message,
                exception,
              },
              'DevicesAndRouting:handleSubmit'
            );
            break;
          }
        }
      }

      const deletedDevices = businessDevices.filter((de) => {
        return deletedDeviceIds.includes(de?.deviceId);
      });

      // delete forward devices if touched and no failures detected
      // ----------------------------------------------------------
      if (!errorMessage && deletedDevices.length) {
        // delete forward number
        // -------------------
        for (const { deviceId: deviceid } of deletedDevices) {
          const data = {
            call: methodNameEnum.DELETE_DEVICE,
            apikey: userAtomContents.apikey,
            businessid: userAtomContents.businessid,
            userid: id,
            deviceid,
          };
          let response!: AxiosResponse<any>;
          let isSuccess;
          try {
            response = await mutation.mutateAsync({
              endpoint: endpointEnum.DELETE_DEVICE,
              payload: data,
            });
            if (response?.data?.success) {
              isSuccess = true;
            }
          } catch (exception: any) {
            // eslint-disable-next-line no-console
            console.error(exception.message);
          }

          // exit if failed
          if (!isSuccess) {
            errorMessage = 'Failed to delete forward number';
            Logger.error(
              {
                errorMessage,
                internalMessage: response?.data?.meta?.message,
              },
              'DevicesAndRouting:handleSubmit'
            );
            break;
          }
        }
      }

      // update dialing device if touched and no failures detected
      // ---------------------------------------------------------
      if (!errorMessage && formikTouched.current?.dialingDeviceId) {
        // update ring group settings
        // const { response, exception, status } = await phpRequest({
        //   methodName: methodNameEnum.UPDATE_DEFAULT_DEVICE,
        //   payload: {
        //     user: id,
        //     device: values.dialingDeviceId,
        //   },
        // });
        const data = {
          call: methodNameEnum.UPDATE_DEFAULT_DEVICE,
          apikey: userAtomContents.apikey,
          businessid: userAtomContents.businessid,
          userid: id,
          device: values.dialingDeviceId,
        };

        let response!: AxiosResponse<any>;
        let isSuccess = false;
        try {
          const response = await mutation.mutateAsync({
            endpoint: endpointEnum.UPDATE_DEFAULT_DEVICE,
            payload: data,
          });
          // check successful
          isSuccess = response?.data?.success;
        } catch (exception: any) {
          // eslint-disable-next-line no-console
          console.error(exception.message);
        }
        // exit if failed
        if (!isSuccess) {
          errorMessage = 'Failed to update dialing device';
          Logger.error(
            {
              errorMessage,
              internalMessage: response?.data?.message,
            },
            'DevicesAndRouting:handleSubmit'
          );
        }
      }

      // if successful then refresh and set status accordingly
      if (!errorMessage) {
        refetchToggleForRingGroupSettings({});
        refetchToggleForBusinessDevices({});
        refetchToggleMyBusinessUsers({});
        onHide();
      }

      // set aggregate status
      setSubmitState(
        errorMessage ? loadableEnum.HAS_ERROR : loadableEnum.HAS_VALUE
      );

      enqueueSnackbarWithStyles({
        message: errorMessage || 'Update successful',
        alertProps: {
          severity: errorMessage ? 'error' : 'success',
        },
      });
      // show notification

      // force reset touched
      action.resetForm();
    },
    [
      id,
      initialValues,
      setSubmitState,
      loadableEnum,
      uploadedFiles,
      phpRequest,
      refetchToggleForRingGroupSettings,
      refetchToggleForBusinessDevices,
      enqueueSnackbarWithStyles,
      deletedDeviceIds,
      uploadedFilesVm,
      refetchToggleMyBusinessUsers,
    ]
  );

  const handleSubmitNewDevice = async (newDevice: NewDevice) => {
    const data = {
      call: methodNameEnum.SUBMIT_NEW_DEVICE,
      apikey: userAtomContents.apikey,
      businessid: userAtomContents.businessid,
      // ...formik.values.submitNewDevice,
      ...newDevice,
      userid: id,
    };

    let response!: AxiosResponse<any>;
    let isSuccess = false;
    try {
      response = await mutation.mutateAsync({
        endpoint: endpointEnum.SUBMIT_NEW_DEVICE,
        payload: data,
      });

      // check successful
      isSuccess = response?.data?.success;
    } catch (exception: any) {
      isSuccess = false;
      // eslint-disable-next-line no-console
      console.error(exception.message);
    }
    return isSuccess;
  };

  /**
   * Batch delete to end of submit
   */
  const handleDeleteForwardDevice = useCallback(
    (
      device: IBusinessDevices,
      formik: FormikProps<IFormObject>,
      arrayHelper: FieldArrayRenderProps,
      index: number
    ) => {
      // if dialing device has been selected then show tooltip and return
      if (device.deviceTextId === formik.values.dialingDeviceId) {
        setTooltipIsOpen(index);
        return;
      }

      setDeletedDevicesIds((old) => [...old, device?.deviceId]);

      // clear forward/ring to number if deleting that number
      if (device.ringSelected || device.forwardSelected) {
        const oneChecked = isOneChecked(
          device.deviceId, // do not include this device in calculation
          false, // pretend we are unchecking
          formik.values.forwardDevices.concat(formik.values.kixieDevices)
        );

        formik.setFieldValue(
          'ringGroupSettings.simultaneous.user-simultaneous-ring',
          oneChecked
        );

        // find first available forward device
        const nextForwardDeviceIndex = formik.values.forwardDevices.findIndex(
          (forwardDevice: any) => forwardDevice.deviceId !== device.deviceId
        );

        // if any forwarded devices after deletion then select first one
        if (nextForwardDeviceIndex !== -1) {
          formik.setFieldValue(
            `forwardDevices.${nextForwardDeviceIndex}.forwardSelected`,
            true
          );
          formik.setFieldValue(
            'ringGroupSettings.forward.members.0',
            formik.values.forwardDevices[nextForwardDeviceIndex].deviceId
          );
        } else {
          formik.setFieldValue('ringGroupSettings.forward.members.0', '');
          formik.setFieldValue(
            'ringGroupSettings.forward.user-forward-ring',
            false
          );
        }
      }
      // remove forward device
      arrayHelper.remove(index);

      // manually touched forward device
      if (!formik.touched.forwardDevices?.length) {
        formik.touched.forwardDevices = [];
      }

      // set forwardNumber as touched
      formik.touched.forwardDevices[index] = {
        deviceId: true, // signify device deleted
      };

      formik.setTouched({
        ...formik.touched,
      });
    },
    [deletedDeviceIds]
  );

  /**
   * Add new Kixie device
   * @param event Mouse click event
   */
  const handleSubmitNewKixieDevice = useCallback(
    (phoneType: 'mobile' | 'desk') => async (
      event: React.MouseEvent<HTMLButtonElement>
    ) => {
      setSubmitState(loadableEnum.LOADING);
      let errorMessage = '';

      const { response, exception, status } = await phpRequest({
        methodName: methodNameEnum.SUBMIT_NEW_KIXIE_DEVICE,
        payload: {
          phoneType,
          userid: id,
        },
      });

      // check successful
      const isSuccess =
        status === loadableEnum.HAS_VALUE && response?.data?.meta?.success;

      // exit if failed
      if (!isSuccess) {
        errorMessage = 'Failed to add kixie cell - please try again';
        Logger.error(
          {
            errorMessage,
            internalMessage: response?.data?.meta?.message,
            exception,
          },
          'DevicesAndRouting:handleAddKixieCell'
        );
      }

      // if successful then refresh and set status accordingly
      if (!errorMessage) {
        refetchToggleForBusinessDevices({});
      }

      // set aggregate status
      setSubmitState(
        errorMessage ? loadableEnum.HAS_ERROR : loadableEnum.HAS_VALUE
      );

      enqueueSnackbarWithStyles({
        message: errorMessage || 'Update successful',
        alertProps: {
          severity: errorMessage ? 'error' : 'success',
        },
      });
    },
    [enqueueSnackbarWithStyles, phpRequest]
  );

  /**
   * Update filename on input change
   * Set filename to name of file or '# files' if multiple files
   *
   * NOTE: input element value must be set with event.target.value
   *
   * @param {Object} event Synthetic React event
   */
  const handleChangeFile = useCallback(
    (type: string) => ({ target: { files } }: any) => {
      setFilenames({
        ...filenames,
        // voicemail or greeting
        [type]:
          files.length > 1 ? `${files.length} files` : files?.[0]?.name ?? '',
      });
      setFilepaths({
        ...filepaths,
        [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
      });
      setUploadedFiles({
        ...uploadedFiles,
        [type]: files,
      }); // take first file
    },
    [filenames, filepaths, uploadedFiles]
  );

  const getEditedForwardDevices = (
    values: IFormObject,
    devices: IBusinessDevices[]
  ) => {
    const editedDevices: IBusinessDevices[] = [];
    // For each device in values, compare it's values to the inital values
    // in businessDevices to determine if edited or not.

    for (const device of businessDevices) {
      if (!device?.forwardNumber) continue;

      const deviceInValues = values.forwardDevices.find(
        (fd) => fd?.deviceId === device?.deviceId
      );

      if (!deviceInValues) continue;

      if (device.countrycode !== deviceInValues.countrycode) {
        editedDevices.push(deviceInValues);
        continue;
      }
      const aa =
        device?.forwardNumber?.[0] === '+'
          ? device.forwardNumber
          : '+' + device.forwardNumber;
      const bb =
        deviceInValues?.forwardNumber?.[0] === '+'
          ? deviceInValues.forwardNumber
          : '+' + deviceInValues.forwardNumber;
      if (aa !== bb) {
        editedDevices.push(deviceInValues);
        continue;
      }
      if (device.deviceName !== deviceInValues.deviceName) {
        editedDevices.push(deviceInValues);
        continue;
      }
      if (device.phoneType !== deviceInValues.phoneType) {
        editedDevices.push(deviceInValues);
        continue;
      }
    }
    return editedDevices;
  };

  const handleChangeFileVm = useCallback(
    (type) => ({ target: { files } }) => {
      setFilenamesVm({
        ...filenamesVm,
        // voicemail or greeting
        [type]:
          files.length > 1 ? `${files.length} files` : files?.[0]?.name ?? '',
      });
      setFilepathsVm({
        ...filepathsVm,
        [type]: files?.[0] && URL.createObjectURL(files?.[0]), // create file blob
      });
      setUploadedFilesVm({
        ...uploadedFilesVm,
        [type]: files,
      }); // take first file
    },
    [filenamesVm, filepathsVm, uploadedFilesVm]
  );

  const handleRecording = useCallback(
    (type: string) => (files: any) => {
      setUploadedFiles({
        ...uploadedFiles,
        [type]: files,
      });
    },
    [uploadedFiles]
  );

  const handleRecordingVm = useCallback(
    (type: string) => (files: any) => {
      setUploadedFilesVm({
        ...uploadedFilesVm,
        [type]: files,
      });
    },
    [uploadedFilesVm]
  );
  //#endregion

  //#region Effects
  //===============
  useEffect(() => {
    setFilenames({
      greeting:
        ringGroupSettings.greeting.type === 'mp3'
          ? ringGroupSettings.greeting.value.split('/').slice(-1).pop() ?? '' // split '/' to get filename
          : '', // filename from uri/path
    });
    setFilepaths({
      greeting:
        ringGroupSettings.greeting.type === 'mp3'
          ? ringGroupSettings.greeting.value
          : '',
    });
  }, [ringGroupSettings]);
  //#endregion

  const [tabIndex, setTabIndex] = useState<number>(initialTabIndex);

  const handleChange = (formik: FormikProps<IFormObject>) => async (
    event: SyntheticEvent<Element, Event>, // Change this to SyntheticEvent
    newIndex: any // Use 'any' for the tab index
  ) => {
    // change tab if form valid
    // if (
    //   formik.isValid || // if valid
    //   !isErrorInTab(tabIndex, formik.errors, tabToFields) || // or error not in tab
    //   !formik.dirty // or not dirty
    // ) {
    setTabIndex(+newIndex);
    // }
  };

  const tabNames = [
    {
      title: 'Routing',
      icon: CallReceivedIcon,
    },
    {
      title: 'Forward Devices',
      icon: PhoneForwardedIcon,
    },
    {
      title: 'Kixie Devices',
      icon: DevicesIcon,
    },
  ];

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <form
          className="form form-label-right"
          onSubmit={formik.handleSubmit}
          noValidate
        >
          <TabContext value={String(tabIndex)}>
            <WizardModal
              title={tabNames[tabIndex].title}
              navigation={
                <>
                  <Tab
                    className={clsx(classes.tab, classes.tabHeader)}
                    label="Inbound Call Flow"
                    disabled={true}
                  />
                  <TabList
                    onChange={handleChange(formik)}
                    aria-label="Inbound Call Flow Settings"
                    orientation="vertical"
                    variant="standard"
                    className={clsx(classes.tabs, {
                      [classes.tabError]: !formik.isValid,
                    })}
                  >
                    {tabNames.map((tabItem, index) => (
                      <Tab
                        // used to override typescript error from doing name={tabItem.title}
                        {...{ name: tabItem.title }}
                        key={index}
                        className={classes.tab}
                        label={
                          <TabLabel
                            index={index}
                            formik={formik}
                            tabToFields={tabToFields}
                            tabItem={tabItem}
                          />
                        }
                        value={String(index)}
                      />
                    ))}
                  </TabList>
                </>
              }
              dialogActions={
                <>
                  <Button
                    className={classes.button}
                    disabled={formik.isSubmitting}
                    variant="contained"
                    color="inherit"
                    disableElevation={true}
                    onClick={() => {
                      onHide();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    className={clsx(classes.button)}
                    variant="contained"
                    disableElevation={true}
                    disabled={formik.isSubmitting || !formik.dirty}
                    onClick={(
                      event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                    ) => {
                      // trigger show errors if errors exists
                      formik.handleSubmit(event as any);

                      // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                      if (
                        !formik.isValid && // if form invalid
                        !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                      ) {
                        const errorTabIndex = getErrorTabIndex(
                          formik.errors,
                          tabToFields
                        );
                        /**
                         * switch to tab containing error
                         *
                         * since form validation is in flight and makes page rendering updates
                         * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                         * putting state changes in a separate tick will prevent coliding page rendering updates
                         *
                         * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                         */
                        setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                      }
                      if (formik.isValid) onHide();
                    }}
                  >
                    Save
                  </Button>
                </>
              }
              show={show}
              onHide={onHide}
              onSubmit={formik.handleSubmit}
              isLoading={
                ringGroupSettingsLoadable.state === 'loading' ||
                businessDevicesLoadable.state === 'loading' ||
                submitState === loadableEnum.LOADING
              }
              isSubmitting={formik.isSubmitting}
              disableSubmit={!formik.dirty}
            >
              {!formik.isSubmitting && // submitting turns all fields to touched
                (formikTouched.current = formik.touched) && <></>}
              <TabPanel value="0">
                {/* <header className={clsx(classes.header)}>Inbound Call Flow</header> */}
                <article /*className={classes.spaceBelowHeader}*/>
                  <aside className={classes.twoColumnLayout}>
                    <RecordNow
                      className={classes.leftColumn}
                      outlineLabel="Initial Greeting"
                      dropdownLabel="Greeting Type"
                      helperText="Set an optional greeting message that will play for callers before your number rings."
                      dropdownName="ringGroupSettings.greeting.type"
                      dropdownType={
                        formik.values.ringGroupSettings.greeting
                          .type as TAudioType
                      }
                      ttsLabel="Greeting Message"
                      ttsName="ringGroupSettings.greeting.value"
                      filenameName="submitQueueGreetingMp3File.files"
                      filesizeName="submitQueueGreetingMp3File.name"
                      formik={formik}
                      filename={filenames.greeting}
                      filepath={filepaths.greeting}
                      onChangeFile={handleChangeFile('greeting')}
                      onRecording={handleRecording('greeting')}
                    >
                      <MenuItem value="none">
                        <em>None</em>
                      </MenuItem>
                      <MenuItem value="tts">Text-to-Speech</MenuItem>
                      <MenuItem value="mp3">MP3</MenuItem>
                      <MenuItem value="record">Record Now</MenuItem>
                    </RecordNow>
                    <LabelledOutline
                      label="Outbound Dialing Device"
                      className={classes.rightColumn}
                    >
                      <FormGroup>
                        <FormikSelect
                          name="dialingDeviceId"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          label="Dialing Device"
                          size="small"
                          variant="outlined"
                          required
                        >
                          {formik.values.kixieDevices
                            .concat(formik.values.forwardDevices)
                            .map((device) => (
                              <MenuItem
                                key={uuidv4()}
                                value={device.deviceTextId}
                              >
                                {getDeviceLabel(
                                  device.deviceName,
                                  device.forwardNumber
                                )}
                              </MenuItem>
                            ))}
                        </FormikSelect>
                        <FormHelperText
                          className={classes.helperText}
                          style={{ marginTop: -5, marginBottom: 0 }}
                        >
                          When ON, the selected device(s) below will ring when
                          your direct line is called.
                        </FormHelperText>
                      </FormGroup>
                    </LabelledOutline>
                  </aside>
                  <LabelledOutline
                    label="Device(s) to Ring"
                    className={classes.spaceBelowHeader}
                  >
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <FormikSwitch
                            name="ringGroupSettings.simultaneous.user-simultaneous-ring"
                            formik={formik}
                            color="primary"
                            type="checkbox"
                            onClick={(event: React.ChangeEvent<any>) => {
                              const oneChecked =
                                !!formik.values.forwardDevices.find(
                                  (device) => device.ringSelected
                                ) ||
                                !!formik.values.kixieDevices.find(
                                  (device) => device.ringSelected
                                );
                              // set first available device if none set
                              if (event.target.checked && !oneChecked) {
                                formik.setFieldValue(
                                  'forwardDevices.0.ringSelected',
                                  true
                                );
                              }
                            }}
                          />
                        }
                        label="Ring Device(s)"
                      />
                      <FormHelperText className={classes.helperText}>
                        When ON, the selected device(s) below will ring when
                        your direct line is called.
                      </FormHelperText>
                    </FormGroup>
                    <FormikTextField
                      name="ringGroupSettings.simultaneous.user-simultaneous-duration"
                      className={classes.field}
                      formik={formik}
                      fullWidth
                      label="Seconds to Ring Device(s)"
                      size="small"
                      required
                      variant="outlined"
                      helperText="(5 seconds = 1 ring)"
                    />
                    <FormGroup>
                      {businessDevicesLoadable.state === 'loading' &&
                      !businessDevices.length ? (
                        <DevicesSkeleton />
                      ) : (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                onClick={(
                                  event: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                  // determine if all is checked
                                  // then do opposite
                                  const oneChecked =
                                    !!formik.values.forwardDevices.find(
                                      (device) => device.ringSelected
                                    ) ||
                                    !!formik.values.kixieDevices.find(
                                      (device) => device.ringSelected
                                    );

                                  // uncheck if checked and vice-versa
                                  formik.values.forwardDevices.forEach(
                                    (device, index: number) => {
                                      formik.setFieldValue(
                                        `forwardDevices.${index}.ringSelected`,
                                        !oneChecked
                                      );
                                    }
                                  );
                                  formik.values.kixieDevices.forEach(
                                    (device, index: number) => {
                                      formik.setFieldValue(
                                        `kixieDevices.${index}.ringSelected`,
                                        !oneChecked
                                      );
                                    }
                                  );
                                  formik.setFieldValue(
                                    'ringGroupSettings.simultaneous.user-simultaneous-ring',
                                    !oneChecked
                                  );
                                }}
                                checked={
                                  // cannot find any unchecked then checked
                                  !formik.values.forwardDevices.find(
                                    (device) => !device.ringSelected
                                  ) &&
                                  !formik.values.kixieDevices.find(
                                    (device) => !device.ringSelected
                                  )
                                }
                                indeterminate={
                                  // find at least one checked and one unchecked
                                  // in both arrays
                                  !!formik.values.forwardDevices
                                    .concat(formik.values.kixieDevices)
                                    .find((device) => device.ringSelected) &&
                                  !!formik.values.forwardDevices
                                    .concat(formik.values.kixieDevices)
                                    .find((device) => !device.ringSelected)
                                }
                              />
                            }
                            label="Select All/None"
                          />
                          <hr className={classes.hr} />
                          <FormControl className={classes.ringScrolling}>
                            <FieldArray
                              name="forwardDevices"
                              render={(arrayHelper) =>
                                formik.values.forwardDevices.map(
                                  (device, index: number) => (
                                    <FormControlLabel
                                      key={index}
                                      control={
                                        <FormikCheckbox
                                          formik={formik}
                                          name={`forwardDevices.${index}.ringSelected`}
                                          color="primary"
                                          onClick={(
                                            event: React.ChangeEvent<any>
                                          ) => {
                                            const oneChecked = isOneChecked(
                                              device.deviceId,
                                              event.target.checked,
                                              formik.values.forwardDevices.concat(
                                                formik.values.kixieDevices
                                              )
                                            );
                                            // enable ring if at least one checked
                                            formik.setFieldValue(
                                              'ringGroupSettings.simultaneous.user-simultaneous-ring',
                                              oneChecked
                                            );
                                          }}
                                        />
                                      }
                                      label={getDeviceLabel(
                                        device.deviceName,
                                        device.forwardNumber
                                      )}
                                    />
                                  )
                                )
                              }
                            />
                            <FieldArray
                              name="kixieDevices"
                              render={(arrayHelper) =>
                                formik.values.kixieDevices.map(
                                  (device, index: number) => (
                                    <FormControlLabel
                                      key={index}
                                      control={
                                        <FormikCheckbox
                                          formik={formik}
                                          name={`kixieDevices.${index}.ringSelected`}
                                          color="primary"
                                          onClick={(
                                            event: React.ChangeEvent<any>
                                          ) => {
                                            const oneChecked = isOneChecked(
                                              device.deviceId,
                                              event.target.checked,
                                              formik.values.forwardDevices.concat(
                                                formik.values.kixieDevices
                                              )
                                            );
                                            // enable ring if at least one checked
                                            formik.setFieldValue(
                                              'ringGroupSettings.simultaneous.user-simultaneous-ring',
                                              oneChecked
                                            );
                                          }}
                                        />
                                      }
                                      label={getDeviceLabel(
                                        device.deviceName,
                                        device.forwardNumber
                                      )}
                                    />
                                  )
                                )
                              }
                            />
                          </FormControl>
                        </>
                      )}
                    </FormGroup>
                  </LabelledOutline>
                  <aside className={classes.twoColumnLayout}>
                    {!!formik.values.forwardDevices.length && (
                      <LabelledOutline
                        label="Call Forwarding"
                        className={clsx(
                          classes.spaceBelowHeader,
                          classes.leftColumn
                        )}
                      >
                        <FormGroup>
                          <FormControlLabel
                            control={
                              <FormikSwitch
                                name="ringGroupSettings.forward.user-forward-ring"
                                formik={formik}
                                color="primary"
                                type="checkbox"
                                onClick={(event: React.ChangeEvent<any>) => {
                                  // set first available device if none set
                                  if (
                                    event.target.checked &&
                                    !formik.values.ringGroupSettings.forward
                                      .members[0]
                                  ) {
                                    formik.setFieldValue(
                                      'forwardDevices.0.forwardSelected',
                                      true
                                    );
                                    formik.setFieldValue(
                                      'ringGroupSettings.forward.members.0',
                                      formik.values.forwardDevices[0].deviceId
                                    );
                                  }
                                  if (
                                    !event.target.checked &&
                                    formik.values.ringGroupSettings.forward[
                                      `user-press-one-ring`
                                    ]
                                  ) {
                                    formik.setFieldValue(
                                      'ringGroupSettings.forward.user-press-one-ring',
                                      false
                                    );
                                  }
                                }}
                              />
                            }
                            label="Forward Call"
                          />
                          <FormHelperText className={classes.helperText}>
                            When ON, Kixie will forward the call to this number
                            when the call is not answered by a prior device.
                          </FormHelperText>
                        </FormGroup>
                        <FormikTextField
                          name="ringGroupSettings.forward.user-forward-duration"
                          className={classes.field}
                          formik={formik}
                          fullWidth
                          label="Seconds to Ring Device(s)"
                          size="small"
                          required
                          variant="outlined"
                          helperText="(5 seconds = 1 ring)"
                        />
                        {businessDevicesLoadable.state === 'loading' &&
                        !businessDevices.length ? (
                          <DevicesSkeleton />
                        ) : (
                          <FormikSelect
                            name="ringGroupSettings.forward.members.0"
                            className={classes.field}
                            formik={formik}
                            fullWidth
                            label="Forward to"
                            size="small"
                            variant="outlined"
                            required
                            onChange={(event: SelectChangeEvent) => {
                              // update forward to enable flag base on chosen dropdown
                              formik.setFieldValue(
                                'ringGroupSettings.forward.user-forward-ring',
                                !!event.target.value
                              );
                            }}
                          >
                            {formik.values.forwardDevices.map((device) => (
                              <MenuItem key={uuidv4()} value={device.deviceId}>
                                {getDeviceLabel(
                                  device.deviceName,
                                  device.forwardNumber
                                )}
                              </MenuItem>
                            ))}
                          </FormikSelect>
                        )}
                      </LabelledOutline>
                    )}
                    <div className={clsx(classes.rightColumn)}>
                      {!!formik.values.forwardDevices.length && (
                        <LabelledOutline
                          label="Announce Call"
                          className={clsx(
                            classes.spaceBelowHeader,
                            classes.leftColumn
                          )}
                        >
                          <FormGroup>
                            <FormControlLabel
                              control={
                                <FormikSwitch
                                  name="ringGroupSettings.forward.user-press-one-ring"
                                  formik={formik}
                                  color="primary"
                                  type="checkbox"
                                  disabled={
                                    !formik.values.ringGroupSettings.forward[
                                      'user-forward-ring'
                                    ]
                                  }
                                />
                              }
                              label="Enable Announce Call"
                            />
                            <FormHelperText className={classes.helperText}>
                              When ON, Kixie will announce &#39;Dial 1 to accept
                              this Kixie call&#39; when the forwarding number
                              answers, which also prevents the call from landing
                              in the forwarding number&#39;s voicemail.
                            </FormHelperText>
                            {!formik.values.ringGroupSettings.forward[
                              'user-forward-ring'
                            ] && (
                              <FormHelperText className={classes.helperText}>
                                You must enable <em>Forward Call</em> to use
                                this feature.
                              </FormHelperText>
                            )}
                          </FormGroup>
                        </LabelledOutline>
                      )}
                    </div>
                  </aside>
                  <RecordNow
                    dropdownLabel="Voicemail Type"
                    dropdownName="accountSettings.voicemailType"
                    dropdownType={
                      formik.values?.accountSettings?.voicemailType as
                        | 'mp3'
                        | 'tts'
                        | 'record'
                    }
                    outlineLabel="Voicemail Message"
                    ttsLabel="Voicemail Message"
                    ttsName="accountSettings.greeting"
                    filenameName="submitVoicemailMp3File.files"
                    filesizeName="submitVoicemailMp3File.name"
                    formik={formik}
                    filename={filenamesVm.voicemail}
                    filepath={filepathsVm.voicemail}
                    onChangeFile={handleChangeFileVm('voicemail')}
                    onRecording={handleRecordingVm('voicemail')}
                    className={clsx(classes.spaceBelowHeader)}
                    helperText="Set a voicemail message for callers to hear when you miss a call."
                  >
                    <MenuItem value="tts">Text-to-Speech</MenuItem>
                    <MenuItem value="mp3">MP3</MenuItem>
                    <MenuItem value="record">Record Now</MenuItem>
                  </RecordNow>
                </article>
              </TabPanel>
              <TabPanel value="1">
                {/* <header className={classes.header}>Forward Devices</header> */}
                <article /*className={classes.spaceBelowHeader}*/>
                  {/* <LabelledOutline
                    label="Forward Devices"
                    className={classes.spaceBelowHeader}
                  > */}
                  {businessDevicesLoadable.state === 'loading' &&
                  !businessDevices.length ? (
                    <DevicesSkeleton />
                  ) : (
                    <FieldArray
                      name="forwardDevices"
                      render={(arrayHelper) => (
                        <>
                          <section className={classes.spaceVertically}>
                            {showAddNumber && (
                              <FormControl className={classes.spaceVertically}>
                                <FormGroup
                                  className={classes.formGroupForDevices}
                                >
                                  <FormikSelect
                                    className={clsx(
                                      classes.field,
                                      classes.spaceHorizontally
                                    )}
                                    name="submitNewDevice.countrycode"
                                    formik={formik}
                                    disabled={
                                      submitState === loadableEnum.LOADING
                                    }
                                    label="Country"
                                    size="small"
                                    variant="outlined"
                                    native
                                  >
                                    {countryGroups.map((
                                      group: string // print out optgroup(s)
                                    ) => (
                                      <optgroup key={group} label={group}>
                                        {countryList
                                          .filter((country: ICountryList) =>
                                            country.groups?.includes(group)
                                          )
                                          .map((country: ICountryList) => (
                                            <option
                                              key={country.code}
                                              value={country.code}
                                            >
                                              {country.name}
                                            </option>
                                          ))}
                                      </optgroup>
                                    ))}
                                    {countryList.map((
                                      country: ICountryList // print out options
                                    ) => (
                                      <option
                                        key={country.code}
                                        value={country.code}
                                      >
                                        {country.name}
                                      </option>
                                    ))}
                                  </FormikSelect>
                                  <FormikTextField
                                    className={clsx(
                                      classes.field,
                                      classes.spaceHorizontally
                                    )}
                                    name="submitNewDevice.forwardNumber"
                                    formik={formik}
                                    disabled={
                                      submitState === loadableEnum.LOADING
                                    }
                                    label="Forward"
                                    size="small"
                                    required
                                    variant="outlined"
                                  />
                                  <FormikTextField
                                    name="submitNewDevice.deviceName"
                                    className={clsx(
                                      classes.field,
                                      classes.spaceHorizontally
                                    )}
                                    formik={formik}
                                    disabled={
                                      submitState === loadableEnum.LOADING
                                    }
                                    label="Device Name"
                                    size="small"
                                    required
                                    variant="outlined"
                                  />
                                  <FormikSelect
                                    name="submitNewDevice.phoneType"
                                    className={clsx(
                                      classes.field,
                                      classes.spaceHorizontally
                                    )}
                                    formik={formik}
                                    disabled={
                                      submitState === loadableEnum.LOADING
                                    }
                                    label="Dialing Device"
                                    size="small"
                                    variant="outlined"
                                  >
                                    <MenuItem value="cell">Cell Phone</MenuItem>
                                    <MenuItem value="desk">Desk Phone</MenuItem>
                                    <MenuItem value="other">
                                      Other Phone
                                    </MenuItem>
                                  </FormikSelect>
                                </FormGroup>
                              </FormControl>
                            )}
                            <summary className={classes.spaceVertically}>
                              <Button
                                className={clsx(classes.spaceHorizontally)}
                                variant="contained"
                                color={showAddNumber ? 'inherit' : 'primary'}
                                startIcon={<PlaylistAddIcon />}
                                disableElevation
                                disabled={submitState === loadableEnum.LOADING}
                                onClick={(
                                  event: React.MouseEvent<HTMLButtonElement>
                                ) => {
                                  setShowAddNumber(!showAddNumber);
                                }}
                              >
                                {showAddNumber ? 'Cancel Add' : 'Add Number'}
                              </Button>
                              {showAddNumber && (
                                <Button
                                  className={classes.spaceHorizontally}
                                  variant="contained"
                                  color="primary"
                                  disableElevation
                                  disabled={
                                    submitState === loadableEnum.LOADING
                                  }
                                  onClick={() => {
                                    formik.setFieldValue('forwardDevices', [
                                      ...formik.values.forwardDevices,
                                      formik.values.submitNewDevice,
                                    ]);
                                    formik.setFieldValue('submitNewDevice', {
                                      forwardNumber: '',
                                      countrycode: 'US',
                                      deviceName: '',
                                      phoneType: 'cell',
                                      userid: '',
                                    });
                                    formik.setErrors({});
                                    setShowAddNumber(false);
                                    formik.setFieldTouched(
                                      'submitNewDevice.forwardNumber',
                                      false
                                    );
                                    formik.setFieldTouched(
                                      'submitNewDevice.deviceName',
                                      false
                                    );
                                  }}
                                >
                                  Confirm
                                </Button>
                              )}
                            </summary>
                          </section>
                          {formik.values.forwardDevices.map(
                            (device, index: number) => {
                              return (
                                <div key={index}>
                                  <FormGroup
                                    key={index}
                                    className={clsx(
                                      classes.formGroupForDevices,
                                      classes.spaceVertically
                                    )}
                                    data-test-forward-device-id={
                                      device.deviceId
                                    }
                                  >
                                    <FormikSelect
                                      className={clsx(
                                        classes.field,
                                        classes.spaceHorizontally
                                      )}
                                      name={`forwardDevices.${index}.countrycode`}
                                      formik={formik}
                                      label="Country"
                                      size="small"
                                      variant="outlined"
                                      native
                                    >
                                      {countryGroups.map((
                                        group: string // print out optgroup(s)
                                      ) => (
                                        <optgroup key={group} label={group}>
                                          {countryList
                                            .filter((country: ICountryList) =>
                                              country.groups?.includes(group)
                                            )
                                            .map((country: ICountryList) => (
                                              <option
                                                key={country.code}
                                                value={country.code}
                                              >
                                                {country.name}
                                              </option>
                                            ))}
                                        </optgroup>
                                      ))}
                                      {countryList.map((
                                        country: ICountryList // print out options
                                      ) => (
                                        <option
                                          key={country.code}
                                          value={country.code}
                                        >
                                          {country.name}
                                        </option>
                                      ))}
                                    </FormikSelect>
                                    <FormikTextField
                                      className={clsx(
                                        classes.field,
                                        classes.spaceHorizontally
                                      )}
                                      name={`forwardDevices.${index}.forwardNumber`}
                                      formik={formik}
                                      label="Forward To"
                                      helperText="Use E.164 phone number format"
                                      size="small"
                                      required
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            +
                                          </InputAdornment>
                                        ),
                                      }}
                                      variant="outlined"
                                    />
                                    <FormikTextField
                                      name={`forwardDevices.${index}.deviceName`}
                                      className={clsx(
                                        classes.field,
                                        classes.spaceHorizontally
                                      )}
                                      formik={formik}
                                      label="Device Name"
                                      size="small"
                                      required
                                      variant="outlined"
                                    />
                                    <FormikSelect
                                      name={`forwardDevices.${index}.phoneType`}
                                      className={clsx(
                                        classes.field,
                                        classes.spaceHorizontally
                                      )}
                                      formik={formik}
                                      label="Dialing Device"
                                      size="small"
                                      variant="outlined"
                                    >
                                      <MenuItem value="cell">
                                        Cell Phone
                                      </MenuItem>
                                      <MenuItem value="desk">
                                        Desk Phone
                                      </MenuItem>
                                      <MenuItem value="other">
                                        Other Phone
                                      </MenuItem>
                                    </FormikSelect>
                                    <Tooltip
                                      title="This number is currently used as an outbound dialing device please select another number under 'Inbound Call Flow'"
                                      aria-label="delete"
                                      open={tooltipIsOpen === index}
                                      onClose={() => setTooltipIsOpen(-1)}
                                    >
                                      <IconButton // enclosed inside empty tag to bypass tooltip error when disabling button
                                        className={clsx(
                                          classes.deleteIconButton,
                                          classes.shortenIcon
                                        )}
                                        aria-label="delete"
                                        onClick={() => {
                                          handleDeleteForwardDevice(
                                            device,
                                            formik,
                                            arrayHelper,
                                            index
                                          );
                                        }}
                                        size="large"
                                      >
                                        <DeleteIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </FormGroup>
                                </div>
                              );
                            }
                          )}
                        </>
                      )}
                    />
                  )}
                  {/* </LabelledOutline> */}
                </article>
              </TabPanel>
              <TabPanel value="2">
                {/* <header className={clsx(classes.header)}>Kixie Devices</header> */}
                <article /*className={classes.spaceBelowHeader}*/>
                  {/* <LabelledOutline
                          label="Kixie Devices"
                          className={classes.spaceBelowHeader}
                        > */}
                  <section /*className={classes.spaceVertically}*/>
                    <Button
                      className={classes.spaceHorizontally}
                      variant="contained"
                      color={showAddKixieCell ? 'inherit' : 'primary'}
                      startIcon={<PhoneIphoneIcon />}
                      hidden={
                        !!formik.values.kixieDevices.find(
                          (device) => device.phoneType === 'mobile softphone'
                        )
                      }
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        setShowAddKixieCell(!showAddKixieCell);
                      }}
                      disabled={
                        businessDevicesLoadable.state === 'loading' ||
                        submitState === loadableEnum.LOADING
                      }
                      disableElevation
                    >
                      {showAddKixieCell ? 'Cancel Add' : 'Add Kixie Cell'}
                    </Button>
                    {showAddKixieCell && (
                      <Button
                        className={classes.spaceHorizontally}
                        variant="contained"
                        color="primary"
                        hidden={
                          !!formik.values.kixieDevices.find(
                            (device) => device.phoneType === 'mobile softphone'
                          )
                        }
                        onClick={(e) => {
                          handleSubmitNewKixieDevice('mobile')(e);
                        }}
                        disabled={
                          businessDevicesLoadable.state === 'loading' ||
                          submitState === loadableEnum.LOADING
                        }
                        disableElevation
                      >
                        Confirm
                      </Button>
                    )}
                    <Button
                      className={classes.spaceHorizontally}
                      variant="contained"
                      color={showAddKixieDesk ? 'inherit' : 'primary'}
                      startIcon={<HeadsetMicIcon />}
                      hidden={
                        !!formik.values.kixieDevices.find(
                          (device) => device.phoneType === 'desk phone'
                        )
                      }
                      disabled={
                        businessDevicesLoadable.state === 'loading' ||
                        submitState === loadableEnum.LOADING
                      }
                      onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                        setShowAddKixieDesk(!showAddKixieDesk);
                        showAddKixieDesk;
                      }}
                      disableElevation
                    >
                      {showAddKixieDesk ? 'Cancel Add' : 'Add Kixie Desk'}
                    </Button>
                    {showAddKixieDesk && (
                      <Button
                        className={classes.spaceHorizontally}
                        variant="contained"
                        color="primary"
                        hidden={
                          !!formik.values.kixieDevices.find(
                            (device) => device.phoneType === 'desk phone'
                          )
                        }
                        disabled={
                          businessDevicesLoadable.state === 'loading' ||
                          submitState === loadableEnum.LOADING
                        }
                        onClick={(e) => {
                          handleSubmitNewKixieDevice('desk')(e);
                        }}
                        disableElevation
                      >
                        Confirm
                      </Button>
                    )}
                  </section>
                  <section>
                    <FormControl
                      component="fieldset"
                      fullWidth
                      className={classes.spaceBelowHeader}
                    >
                      {businessDevicesLoadable.state === 'loading' &&
                      !businessDevices.length ? (
                        <DevicesSkeleton />
                      ) : (
                        <FieldArray
                          name="kixieDevices"
                          render={(arrayHelper) =>
                            formik.values.kixieDevices.map(
                              (device, index: number) => (
                                <FormGroup
                                  key={device.deviceId}
                                  className={classes.formGroupForDevices}
                                  data-test-kixie-device-id={device.deviceId}
                                >
                                  <FormikTextField
                                    className={clsx(
                                      classes.field,
                                      classes.spaceHorizontally
                                    )}
                                    name={`kixieDevices.${index}.deviceName`}
                                    InputProps={{
                                      readOnly:
                                        device.phoneType === 'PowerCall',
                                    }}
                                    formik={formik}
                                    label="Device Name"
                                    size="small"
                                    variant="outlined"
                                    required
                                  />
                                  <FormikTextField
                                    className={clsx(
                                      classes.field,
                                      classes.spaceHorizontally
                                    )}
                                    name={`kixieDevices.${index}.phoneType`}
                                    InputProps={{
                                      readOnly: true,
                                    }}
                                    formik={formik}
                                    label="Dialing Device"
                                    size="small"
                                    variant="outlined"
                                  />
                                  {formik.values.kixieDevices[index].sipUser ? (
                                    <IconButton
                                      className={classes.shortenIcon}
                                      aria-label="info"
                                      color={
                                        kixieDeviceIndex === index
                                          ? 'primary'
                                          : 'inherit'
                                      }
                                      onClick={(
                                        event: React.MouseEvent<HTMLElement>
                                      ) => {
                                        // reset kixieDeviceIndex if already on
                                        setKixieDeviceIndex(
                                          index === kixieDeviceIndex
                                            ? -1
                                            : index
                                        );
                                      }}
                                      size="large"
                                    >
                                      <VpnKeyIcon />
                                    </IconButton>
                                  ) : (
                                    <span className={classes.iconSpacer}></span>
                                  )}
                                </FormGroup>
                              )
                            )
                          }
                        />
                      )}
                      {kixieDeviceIndex !== -1 && (
                        <FormGroup className={classes.formGroupForDevices}>
                          <TextField
                            className={clsx(
                              classes.field,
                              classes.spaceHorizontally
                            )}
                            label="SIP Username"
                            value={
                              formik.values.kixieDevices?.[kixieDeviceIndex]
                                ?.sipUser ?? ''
                            }
                          />
                          <TextField
                            className={clsx(
                              classes.field,
                              classes.spaceHorizontally
                            )}
                            label="SIP Password"
                            value={
                              formik.values.kixieDevices?.[kixieDeviceIndex]
                                ?.sipPass ?? ''
                            }
                          />
                          <Tooltip title="Credentials can be used to log into your SIP client">
                            <IconButton
                              className={classes.helpIcon}
                              aria-label="info"
                              size="large"
                            >
                              <HelpOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </FormGroup>
                      )}
                    </FormControl>
                  </section>
                  {/* </LabelledOutline> */}
                </article>
              </TabPanel>
            </WizardModal>
          </TabContext>
        </form>
      )}
    </Formik>
  );
}
